import {
  Button,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@beweegbaas/ui";
import type { QueryClient } from "@tanstack/react-query";
import type { ReactElement, ReactNode } from "react";
import { RouteDialog } from "#/components/route-dialog";
import { useSchoolLayoutData } from "#/modules/generic.user/use-schools";
import { RoosterEditor } from "#/modules/les.planning/components/rooster-editor";
import {
  planningKlassenQueryOptions,
  roosterQueryOptions,
} from "#/modules/les.planning/query.planning";

// The rooster as a dialog over Mijn lesdag and Mijn lesweek, so a docent sees and changes it without
// leaving the day. Each page has its own `rooster` child route; closing goes back to that page.

export const roosterRouteOptions = {
  staticData: { overlay: true },
  loader: ({
    context,
    params: { school, schoolyear },
  }: {
    context: { queryClient: QueryClient };
    params: { school: string; schoolyear?: string };
  }) =>
    Promise.all([
      context.queryClient.ensureQueryData(roosterQueryOptions({ school, schoolyear })),
      context.queryClient.ensureQueryData(planningKlassenQueryOptions({ school, schoolyear })),
    ]),
};

/** Opens the rooster dialog of the page it sits on; `render` is the link to its route. */
export function RoosterButton({ render }: { render: ReactElement }) {
  const label = m["planning.rooster.title"]();
  return (
    <Button
      variant="outline"
      size="lg"
      className="px-3"
      aria-label={label}
      nativeButton={false}
      render={render}
    >
      <IconMaterialSymbolsCalendarViewWeekOutline className="text-muted-foreground size-4" />
      <span className="max-sm:hidden">{label}</span>
    </Button>
  );
}

/**
 * Needs a `PlanningProvider` around it. `weekoverzicht` and `vakanties` link to their dialogs, which
 * open over this one as `children`. `onClose` goes back to the page it opened over.
 */
export function RoosterRouteDialog({
  params,
  weekdag,
  onWeekdagChange,
  weekoverzicht,
  vakanties,
  onClose,
  children,
}: {
  params: { school: string; schoolyear?: string };
  weekdag: number;
  onWeekdagChange: (weekdag: number) => void;
  weekoverzicht: ReactElement;
  vakanties: ReactElement;
  onClose: () => void;
  children?: ReactNode;
}) {
  const schoolName = useSchoolLayoutData()?.schoolName ?? params.school;
  return (
    <RouteDialog onClose={onClose}>
      <DialogContent className="md:h-[min(56rem,92svh)] md:max-w-6xl" data-testid="rooster-dialog">
        <DialogHeader className="gap-4 md:flex-row md:items-start md:pr-8">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <DialogTitle>{m["planning.rooster.gymrooster"]({ school: schoolName })}</DialogTitle>
            <DialogDescription className="flex max-w-3xl flex-col">
              <span>{m["planning.rooster.uitleg"]()}</span>
              <span>{m["planning.rooster.uitleg_tijden"]()}</span>
            </DialogDescription>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2 self-start">
            <Button
              variant="outline"
              size="lg"
              // A phone is too narrow for five days side by side.
              className="max-md:hidden"
              nativeButton={false}
              render={weekoverzicht}
            >
              <IconMaterialSymbolsCalendarViewWeekOutline />
              {m["planning.rooster.weekoverzicht"]()}
            </Button>
            <Button variant="outline" size="lg" nativeButton={false} render={vakanties}>
              <IconMaterialSymbolsCalendarMonthOutline />
              {m["planning.vakanties.knop"]()}
            </Button>
          </div>
        </DialogHeader>
        {/* The rooster's rows run to the dialog's edges, as they run to the page's. */}
        <DialogBody className="flex flex-col [--container-gutter:--spacing(6)]">
          <RoosterEditor
            tenant={{ school: params.school, schoolyear: params.schoolyear }}
            weekdag={weekdag}
            onWeekdagChange={onWeekdagChange}
          />
        </DialogBody>
        {children}
      </DialogContent>
    </RouteDialog>
  );
}
